import { Orders } from '@database/types';
import {
  FinderStatus,
  type ProductCandidate,
  type ProductFinderResult,
  type ReceiptItem,
  type ReceiptReadResult,
} from '../constants/product-finder';
import ApiBase from './ApiBase';

export default class OrderService extends ApiBase {
  constructor(apiBaseUrl: string) {
    super(apiBaseUrl);
    this.baseUrl = this.baseUrl;
  }

  async find(email: string, orderNumber: string, storeId?: string) {
    const params: Record<string, string> = {
      email: email.trim(),
      order: orderNumber.trim(),
    };

    if (storeId) {
      params.storeId = storeId;
    }

    const { data } = await this.client.get(`/order-lookup/claim`, {
      params,
    });

    return data;
  }

  /**
   * Live platform availability for the order the guest session is bound to (ENG-6074). The lookup
   * response answers the same question from our twice-daily cache; this is what the resolution page
   * should believe when the two disagree.
   *
   * Whether it can be sent, never how many — a guest surface must not carry stock counts.
   *
   * Returns an empty list rather than throwing when the platform cannot be reached, so a caller that
   * finds no line for an item treats it as unknown and leaves the reship option alone.
   */
  async getLiveOrderInventory(storeId: string, sourceOrderId: string) {
    const { data } = await this.client.get(`/customer-portal/orders/${sourceOrderId}/inventory`, {
      params: { storeId },
    });

    return (data?.items ?? []) as { id: string; variantId: string | null; inStock: boolean }[];
  }

  /**
   * Live availability for catalog rows the search returned (ENG-6074). Shopify only — other
   * platforms come back empty and the caller keeps the cached values.
   *
   * Variant and product ids go separately, and each row says which it is: a bare number cannot tell
   * the two apart, and the sequences can collide.
   */
  async getLiveProductInventory(storeId: string, ids: { variantIds: string[]; productIds: string[] }) {
    if (!ids.variantIds.length && !ids.productIds.length) return [];

    const { data } = await this.client.get(`/customer-portal/products/inventory`, {
      params: {
        storeId,
        variantIds: ids.variantIds.join(','),
        productIds: ids.productIds.join(','),
      },
    });

    return (data?.items ?? []) as { id: string; kind: 'variant' | 'product'; inStock: boolean }[];
  }

  async getProductChildren(storeId: string, sourceProductId: string) {
    const { data } = await this.client.get(`/orders/${storeId}/products/${sourceProductId}/children`);

    return data as {
      sourceProductId: string;
      sourceVariantId: string | null;
      sku: string | null;
      name: string | null;
      productImage: string | null;
      price: number;
      connectionType: Orders.ProductConnectionType;
    }[];
  }

  async searchProducts(body: {
    productName: string;
    storeId: string;
    limit: number;
    skip: number;
    size?: string | null;
    colorFamily?: string | null;
  }) {
    const { data } = await this.client.get(`/orders/${body.storeId}/products/query`, {
      params: {
        name: body.productName,
        limit: body.limit,
        skip: body.skip,
        size: body.size || undefined,
        colorFamily: body.colorFamily || undefined,
      },
    });

    return data;
  }

  async getProductSearchFilters(body: {
    productName: string;
    storeId: string;
    size?: string | null;
    colorFamily?: string | null;
  }): Promise<{ sizes: { value: string; count: number }[]; colorFamilies: { value: string; count: number }[] }> {
    const { data } = await this.client.get(`/orders/${body.storeId}/products/query/filters`, {
      params: {
        name: body.productName,
        size: body.size || undefined,
        colorFamily: body.colorFamily || undefined,
      },
    });

    return data;
  }

  async findProducts(body: {
    storeId: string;
    name: string;
    description?: string;
    purchasedAt?: string;
    claimCategory?: string;
  }) {
    const { data } = await this.client.post(`/customer-portal/products/find`, {
      storeId: body.storeId,
      name: body.name,
      description: body.description,
      purchasedAt: body.purchasedAt,
      claimCategory: body.claimCategory,
    });

    // Anything but an explicit OK is treated as unavailable: an empty list carries no reason of its
    // own, and claiming the catalog holds no match is the one answer that stops a customer looking.
    return {
      candidates: (data?.candidates ?? []) as ProductCandidate[],
      status: data?.status === FinderStatus.OK ? FinderStatus.OK : FinderStatus.UNAVAILABLE,
      // Anything that is not a string means we could not tell what kind of thing they own, which the
      // replacement picker treats as a reason not to offer substitutes at all.
      productType: typeof data?.productType === 'string' && data.productType.trim() ? data.productType : null,
    } as ProductFinderResult;
  }

  /**
   * Reads the products off the receipt images the customer already uploaded. Best-effort by
   * design: anything other than an explicit OK means nothing was read, and the form stays empty
   * rather than showing a guess we cannot stand behind.
   */
  async readReceipt(body: { storeId: string; imageUrls: string[] }) {
    const { data } = await this.client.post(`/customer-portal/products/receipt`, {
      storeId: body.storeId,
      imageUrls: body.imageUrls,
    });

    return {
      items: (data?.items ?? []) as ReceiptItem[],
      status: data?.status === FinderStatus.OK ? FinderStatus.OK : FinderStatus.UNAVAILABLE,
    } as ReceiptReadResult;
  }

  /**
   * Products like the given one that the store can actually send. Costs no chat call — one
   * embedding plus a dot product — so a failure here narrows the replacement picker to the
   * candidates already on screen rather than blocking it.
   */
  async findSimilar(body: { storeId: string; name: string; limit?: number }) {
    const { data } = await this.client.post(`/customer-portal/products/similar`, {
      storeId: body.storeId,
      name: body.name,
      limit: body.limit,
    });

    return (data?.candidates ?? []) as ProductCandidate[];
  }
}
